import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useNinpoCore } from './app/hooks/useNinpoCore';
import { CoreProvider } from './lib/coreContext';
import AppShellLayout from './layouts/appShellLayout';
import { getDeploySurface, surfaceRequiresPrivilegedLogin } from './lib/deploySurface';
import { FEATURE_ROUTES } from './app/routeConstants';

import CartDrawer from './features/cart/components/CartDrawer';
import FloatingDock from './app/components/FloatingDock';
import LegalFooter from './app/components/LegalFooter';
import CookieConsentBanner from './app/components/CookieConsentBanner';
import PushConsentPrompt from './app/components/PushConsentPrompt';
import { SupportChatbot } from './features/ai/components/SupportChatbot';
import SupportTicketDrawer from './features/support/components/SupportTicketDrawer';

import { ShoppingBag, MessageCircle } from 'lucide-react';
import { CART_MAX_QUANTITY_PER_ITEM, cartLineId, stripCartLineTempSuffix } from './features/cart/utils/cartNormalization';
import { APP_ROLES, normalizeRole } from './features/auth/rbac';

export type { AppRouteContext } from './app/routeContext';

function App() {
  const core = useNinpoCore();
  const location = useLocation();
  const navigate = useNavigate();

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasWarnedCloudinary, setHasWarnedCloudinary] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const openLoginRef = useRef<(() => void) | null>(null);
  const surface = getDeploySurface();
  const openLogin = () => {
    openLoginRef.current?.();
  };

  const findProductByIdentifier = (identifier: string) => {
    const trimmed = String(identifier || '').trim();
    if (!trimmed) return undefined;
    const upper = trimmed.toUpperCase();
    return core.products.find(
      p =>
        p.id === trimmed ||
        (p as { _id?: string })._id === trimmed ||
        p.frontendId === trimmed ||
        p.productId === trimmed ||
        (p.sku && p.sku.trim().toUpperCase() === upper) ||
        (p.upc && p.upc.trim() === trimmed)
    );
  };

  const getCanonicalCartId = (identifier: string) => {
    const product = findProductByIdentifier(identifier);
    return product?.id || (product as { _id?: string })?._id || product?.frontendId || String(identifier || '').trim();
  };
  
  const addToCart = (
    productId: string,
    options?: { temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either' }
  ) => {
    const product = findProductByIdentifier(productId);
    const stock = product?.stock ?? 0;
    const canonicalCartId = getCanonicalCartId(productId);
    const rawPref = options?.temperaturePreference;
    const temperaturePreference =
      rawPref === 'chilled' || rawPref === 'cold' ? 'chilled' : 'ambient';
    const lineId = cartLineId(canonicalCartId, temperaturePreference);
    const lineMatches = (i: { id?: string; productId?: string; temperaturePreference?: string }) => {
      const id = String(i.id || '').trim();
      if (id === lineId) return true;
      const pid = stripCartLineTempSuffix(String(i.productId || i.id || '').trim());
      const pref =
        i.temperaturePreference === 'chilled' || i.temperaturePreference === 'cold' ? 'chilled' : 'ambient';
      return pid === canonicalCartId && pref === temperaturePreference;
    };
    const productQty = (rows: Array<{ id?: string; productId?: string; quantity?: number }>) =>
      rows
        .filter((i) => {
          const pid = stripCartLineTempSuffix(String(i.productId || i.id || '').trim());
          return pid === canonicalCartId;
        })
        .reduce((sum, i) => sum + Math.max(0, Number(i.quantity || 0)), 0);

    if (stock <= 0) {
      core.addToast('OUT OF STOCK', 'warning');
      return;
    }

    if (productQty(core.cart) >= stock) {
      core.addToast(`MAX STOCK REACHED (${stock})`, 'warning');
      return;
    }

    core.setCart((prev) => {
      if (productQty(prev) >= stock) return prev;
      const existing = prev.find((i) => lineMatches(i as { id?: string; productId?: string; temperaturePreference?: string }));
      if (existing) {
        const others = productQty(prev) - existing.quantity;
        return prev.map((i) =>
          lineMatches(i as { id?: string; productId?: string; temperaturePreference?: string })
            ? {
                ...i,
                id: lineId,
                productId: canonicalCartId,
                quantity: Math.min(CART_MAX_QUANTITY_PER_ITEM, stock - others, i.quantity + 1),
                temperaturePreference
              }
            : i
        );
      }
      return [
        ...prev,
        {
          id: lineId,
          productId: canonicalCartId,
          quantity: 1,
          temperaturePreference
        }
      ];
    });

    core.addToast('ADDED TO CART', 'success');
  };

  useEffect(() => {
    if (hasWarnedCloudinary) return;

    const isStaffConsoleRoute =
      location.pathname.startsWith('/admin') ||
      location.pathname.startsWith('/owner') ||
      location.pathname.startsWith('/management');

    const isStaffUser = (() => {
      if (!core.currentUser) return false;
      const normalizedRole = normalizeRole(core.currentUser.role);
      return normalizedRole === APP_ROLES.owner || normalizedRole === APP_ROLES.admin;
    })();

    if (isStaffConsoleRoute && isStaffUser && !hasWarnedCloudinary) {
      // Staff-only notification if specific integrations need attention
      setHasWarnedCloudinary(true);
    }
  }, [core, core.addToast, core.currentUser?.role, hasWarnedCloudinary, location.pathname]);

  const cartCount = core.cart
    .filter(item => {
      if (item.quantity <= 0) return false;
      const productId = String(item.productId || item.id || '')
        .trim()
        .replace(/:(chilled|ambient)$/i, '');
      if (!productId) return false;
      return (core.products || []).some(
        p =>
          p.frontendId === productId ||
          p.id === productId ||
          (p as { _id?: string })._id === productId
      );
    })
    .reduce((sum, item) => sum + item.quantity, 0);
  const isHomepage = location.pathname === '/';
  const isCheckout =
    location.pathname === '/checkout' ||
    location.pathname.startsWith('/success') ||
    location.pathname.startsWith('/cancel');
  const isStaff =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/owner') ||
    location.pathname.startsWith('/management') ||
    location.pathname.startsWith('/driver');

  // Show cart dock on catalog OR whenever the cart has items (account/home still need access)
  const isShoppingPage =
    location.pathname === FEATURE_ROUTES.customer.shop ||
    location.pathname.startsWith(`${FEATURE_ROUTES.customer.shop}/`) ||
    location.pathname === FEATURE_ROUTES.customer.catalog ||
    location.pathname.startsWith(`${FEATURE_ROUTES.customer.catalog}/`) ||
    location.pathname === FEATURE_ROUTES.customer.products ||
    location.pathname.startsWith(`${FEATURE_ROUTES.customer.products}/`);

  const showDock = (isShoppingPage || cartCount > 0) && !isCheckout && !isStaff;
  const hideCustomerUi = isStaff;

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('cart') === 'open') {
      setIsCartOpen(true);
    }
  }, [location.search]);

  React.useEffect(() => {
    if (!(isShoppingPage || cartCount > 0) && isCartOpen) {
      setIsCartOpen(false);
    }
  }, [location.pathname, isShoppingPage, cartCount, isCartOpen]);

  const topActions = showDock ? (
    <FloatingDock
      cartCount={cartCount}
      onOpenCart={() => setIsCartOpen(true)}
      onToggleChat={() => setIsChatOpen(prev => !prev)}
    />
  ) : null;

  const overlays = (
    <>
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => {
          setIsCartOpen(false);
          const params = new URLSearchParams(location.search);
          if (params.get('cart') === 'open') {
            params.delete('cart');
            const qs = params.toString();
            navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
          }
        }}
      />

      <SupportChatbot
        currentUser={core.currentUser}
        recentOrders={core.orders || []}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        showFloatingButton={false}
      />
      <SupportTicketDrawer
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        notify={(message, variant) => core.addToast(message, variant ?? 'info')}
      />
      {!hideCustomerUi && <LegalFooter />}
      <CookieConsentBanner />
      {core.currentUser ? <PushConsentPrompt /> : null}
    </>
  );

  return (
    <CoreProvider value={core}>
      <AppShellLayout
        core={core}
        surface={surface}
        requirePrivilegedLogin={surfaceRequiresPrivilegedLogin(surface)}
        onOpenSupport={() => setIsChatOpen(true)}
        additionalOverlays={overlays}
        openLoginRef={openLoginRef}
        topActions={topActions}
      >
        <Outlet context={{ core, openLogin, addToCart, findProductByIdentifier, getCanonicalCartId }} />
      </AppShellLayout>
    </CoreProvider>
  );
}

export default App;
